---
title: "Getting started with Chosen"
description: "Set up your organization, invite your team, connect Gmail and Calendar, and post your first job — the order to do it in and what each step is for."
category: "getting-started"
keywords:
  - "Chosen ATS setup"
  - "applicant tracking system onboarding"
  - "getting started recruiting software"
  - "post first job"
schema: "HowTo"
nav:
  section: "getting-started"
  order: 10
---

Setting up Chosen takes about fifteen minutes, and most of that is waiting for Google's OAuth screens to load. This page is the order to do things in — each step links to the details.

By the end you'll have an organization, a team, a connected inbox and calendar, and your first job live on a public careers page.

## The setup order

<Steps>
  <li>**Create your account.** Sign in at [hire.chosenhq.com](https://hire.chosenhq.com). The first person from your company to sign up becomes the **Owner** — [accounts and roles](/getting-started/accounts-and-roles) covers what that means.</li>
  <li>**Invite your team.** Add recruiters and hiring managers and give each one a role. Hiring managers usually want Viewer, not Admin — [accounts and roles](/getting-started/accounts-and-roles) explains why.</li>
  <li>**Connect Gmail.** This powers outreach, reply tracking, and the scheduling assistant. [Connect Gmail](/getting-started/integrations/gmail).</li>
  <li>**Connect Google Calendar.** The AI scheduler needs it to see when you're free. [Connect Calendar](/getting-started/integrations/google-calendar).</li>
  <li>**Connect Notion and Zoom, if you use them.** Both are optional — [Notion](/getting-started/integrations/notion) keeps a synced database, [Zoom](/getting-started/integrations/zoom) adds call links to interviews.</li>
  <li>**Post your first job.** Write it, set the application questions, publish it. [Post your first job](/getting-started/post-first-job).</li>
</Steps>

## You don't have to do all of it now

Chosen works with nothing connected — you can post a job and review applicants on day one. The integrations each unlock something specific, and you can add them the day you need them:

- **Gmail** — sending candidate emails and outreach from your own address.
- **Calendar** — the AI scheduler. Without it, scheduling is manual.
- **Notion** — a two-way synced copy of your candidates and jobs.
- **Zoom** — Zoom links on scheduled interviews. Without it you get Google Meet.

<Callout type="tip" title="Connect Gmail and Calendar before your first interview, not your first job">
Posting a job and collecting applications needs no integrations at all. Connect Gmail and Calendar when you're ready to start scheduling — that's the first point they actually matter.
</Callout>

## Where to go next

Once applications start landing, [working with candidates](/guides/candidates) is where you'll spend most of your time. When you're ready to go find people instead of waiting for them to apply, read [sourcing and outreach](/guides/sourcing).
